import React from 'react';
import { Dropdown, Menu, Tooltip } from 'antd';
import { LEVELS, LEVEL_COLORS, LEVEL_LABELS, MANUAL_DOT_STYLE, SUGGESTED_BORDER } from './rateCalendarState';
import { FONT } from "../../../styles/typography";

const WEEKDAY_HEADERS = ['日', '一', '二', '三', '四', '五', '六'];

// 選單裡「套用建議」「重設」與五個級別共用同一個 onClick，用專屬 key 分流，
// 不會跟 price_types 的級別 id 撞名。
const RESET_KEY = '__reset__';
const ADOPT_KEY = '__adopt__';

// 與 antd Tooltip 的 mouseEnterDelay 預設值 (0.1 秒) 相同 —— 自己接管顯示後
// 這個延遲也得自己來，否則滑鼠掃過日曆時每一格都會瞬間彈出 tooltip。
const HOVER_DELAY_MS = 100;

/** 選單／圖例共用的級別色塊。 */
function LevelSwatch({ level }) {
    return (
        <span
            style={{
                display: 'inline-block',
                width: 12,
                height: 12,
                marginRight: 8,
                verticalAlign: '-1px',
                backgroundColor: LEVEL_COLORS[level],
                border: '1px solid rgba(0,0,0,0.15)',
            }}
        />
    );
}

/** 百分比。null 一律講成「無資料」，不要印成 0% 讓人以為那天真的沒人住。 */
function formatPercent(value) {
    return value == null ? '無資料' : `${value}%`;
}

/** 金額取整數加千分位 —— ADR / RevPAR 看的是量級，小數點沒有判讀價值。 */
function formatMoney(value) {
    return value == null ? '無資料' : Math.round(value).toLocaleString('zh-TW');
}

// 粉紅與橘的底色上，黑字比白字清楚；紅底則反過來
const TEXT_COLOR = {
    'special-holiday': '#ffffff',
};

function pad(n) {
    return String(n).padStart(2, '0');
}

/** 該月第一天的星期，決定前面要空幾格。 */
function leadingBlanks(year, month) {
    return new Date(year, month - 1, 1).getDay();
}

function daysInMonth(year, month) {
    return new Date(year, month, 0).getDate();
}

/**
 * 對應日加上星期，例如 `2025-08-13 (三)`。
 *
 * tooltip 顯示的是那一天的**實際**數字 (day.history[].actual)，不是中位數，
 * 所以這裡要把確切日期講出來 —— 使用者要判斷的是「2025 年那一天到底住幾成」，
 * 講成「8月週三中位數」就答不了這個問題。判定用的中位數仍在後端運作，
 * 有建議時會出現在 reason 那一行。
 *
 * 用 Date.UTC 而不是直接 new Date(matchedDate)，避免依執行環境的時區把
 * 'Y-m-d' 這種不帶時間的字串解讀成前一天。
 */
function matchedDateLabel(matchedDate) {
    const [y, m, d] = matchedDate.split('-').map(Number);
    const weekday = new Date(Date.UTC(y, m - 1, d)).getUTCDay();
    return `${matchedDate} (${WEEKDAY_HEADERS[weekday]})`;
}

// 節日名稱、建議理由、歷史實績、警告，四段各自獨立判斷有沒有東西可顯示 ——
// 沒有歷史實績的日子 (例如新開幕不久的假日) 仍要看得到節日名稱，不能讓
// 「沒有歷史實績」這一個條件連帶吃掉其他三段。
export function DayTip({ day }) {
    const hasHistory = day.history && day.history.length > 0;
    // 建議本身要講出來，不能只給理由 —— 理由講的是「為什麼」，但使用者要先知道
    // 「建議把這天改成什麼」才判斷得了要不要採納。日曆上的藍框只說明「有建議」，
    // 說不出建議的內容。採納的入口就在同一格的選單裡 (套用建議)。
    const hasSuggestion = day.suggested_level && day.suggested_level !== day.base_level;
    return (
        <div>
            {day.holiday_name ? (
                <div data-testid="holiday-name" style={{ fontWeight: 600, marginBottom: 4 }}>
                    {day.holiday_name}
                </div>
            ) : null}
            {hasSuggestion ? (
                <div data-testid="suggestion" style={{ fontWeight: 600, marginBottom: 2 }}>
                    建議：{LEVEL_LABELS[day.base_level]} → {LEVEL_LABELS[day.suggested_level]}
                </div>
            ) : null}
            {day.reason ? <div style={{ marginBottom: 4 }}>{day.reason}</div> : null}
            {hasHistory ? (
                // 三個指標一起看才判斷得出「住房率高」是真有需求還是靠降價換來的：
                // ADR 的分母是賣掉的房間、RevPAR 的分母是可賣的房間，兩者一起
                // 對照住房率，才知道這天值不值得升級。
                //
                // 顯示的是對應日**當天的實際值** (row.actual)，不是判定用的中位數 ——
                // 使用者要看的是三年各自真的發生了什麼。判定仍用中位數 (平滑掉單日的
                // 包場、颱風)，有建議時那個數字會出現在上面的 reason 那一行。
                day.history.map((row) => (
                    <div key={row.year} style={{ marginBottom: 2 }}>
                        <div style={{ opacity: 0.75 }}>
                            {row.matched_date ? matchedDateLabel(row.matched_date) : row.year}
                        </div>
                        <div>
                            住房率 {formatPercent(row.actual ? row.actual.occupancy : null)}
                            　ADR {formatMoney(row.actual ? row.actual.adr : null)}
                            　RevPAR {formatMoney(row.actual ? row.actual.revpar : null)}
                        </div>
                    </div>
                ))
            ) : (
                <div>無歷史實績</div>
            )}
            {day.warning ? <div style={{ marginTop: 4 }}>{day.warning}</div> : null}
        </div>
    );
}

/**
 * 單一日期格：底色是級別、hover 出 tooltip、點擊出級別選單。
 *
 * 四個視覺通道各自獨立、互不干擾：
 * - 底色：價格級別 (平日 / 旺日 / 假日 / 旺假日 / 大假日)。
 * - 藍色邊框：資料對這天有建議 (尚未採納)。底色仍是規則算出的級別，建議並未
 *   套用；要接受建議請到調整建議清單按「採納」，那才會變成人工指定 (右上角
 *   的點) 並真的改變底色。
 * - 右上角黑點：人工指定過，一律覆蓋規則與建議。
 * - 日期數字加底線：這天有節日名稱 (holiday_name，國定假日、補假、小年夜、
 *   除夕等)。這個通道跟級別**正交**，不是級別的附屬品 —— 有節日名稱不代表
 *   級別高，例如連假最後一天的國定假日常常仍是平日 (旅客已陸續回程)，這正是
 *   使用者需要底線提醒的原因：光看底色會以為那天沒什麼特別，不逐格 hover
 *   看不出它其實是節日。
 *
 * tooltip 與級別選單是兩個獨立浮層，錨在同一格上，本來會同時出現 —— 滑鼠停在
 * 格子上叫出 tooltip，接著按下去叫出選單，兩者疊在一起。全站的 assets/layout.sass
 * 把 .ant-tooltip 釘在 z-index: 999999999 (遠高於 dropdown 的 1050)，而 tooltip
 * 沒有 pointer-events: none，於是重疊區域的選單項目全被 tooltip 接走點擊，
 * 使用者選不到那幾個級別。那條 z-index 是全站共用的，不能為了這一頁調動。
 *
 * 解法是「選單開著時不顯示 tooltip」，但關鍵在切換的是**顯示與否**而不是**內容**：
 * 早期版本改用清空 title 讓 antd 自行隱藏 (未受控的 Tooltip 在 title 為空時會強制
 * 隱藏)，看似省事，實際上內容一變 rc-trigger 就重新量測、重新對齊，畫面上會閃出
 * 一個空盒子並跳位 (實測連續三幀分別落在畫面外、原位、0×0)。所以改成自己拿著
 * hover 狀態、把 visible 接管過來，DayTip 的內容從頭到尾不變，只有顯示被關掉。
 *
 * anyMenuOpen 是「任何一格」而不只是自己這格：選單是浮層，滑鼠移過去的路徑上
 * 會掠過別的格子，掠到的那格照樣會彈出 tooltip 蓋住選單。
 *
 * 另外 tooltip 浮層一律 pointer-events: none —— 內容是純文字、不需要接滑鼠，
 * 這樣就算哪個狀態邊角讓它留在畫面上，也不再有能力吃掉任何點擊。
 */
function DayCell({ day, date, anyMenuOpen, onMenuVisibleChange, onChangeLevel, onReset }) {
    // 接管 Tooltip 的 visible 之後，hover 狀態就得自己拿著 —— 而且必須用自己的
    // onMouseEnter / onMouseLeave，不能沿用 Tooltip 的 onVisibleChange。
    //
    // rc-trigger 的 setPopupVisible 只在「新值與 state.popupVisible 不同」時才通知
    // 外界 (rc-trigger/lib/index.js:533-546)，而受控時那個 state 跟著我們給的
    // visible 走。選單開著時 visible 被壓成 false，於是滑鼠**進入**別的格子會
    // 通知 (false→true)，**離開**時因為 false === false 就不通知了 —— 那一格的
    // hovered 永遠卡在 true。選單一關，所有被滑鼠掃過的格子會同時把 tooltip
    // 打開，而且再也收不回去 (它們的離開通知永遠不會來)。
    //
    // 錯在「一邊覆寫 rc-trigger 用來比對的值、一邊又依賴它的通知」。自己掛
    // DOM 事件就沒有這個耦合：離開一定收得到，狀態一定回得去。
    const [hovered, setHovered] = React.useState(false);

    // antd 的 mouseEnterDelay 預設 0.1 秒。自己接管顯示後那個延遲也一起接管過來，
    // 否則滑鼠掃過日曆時每一格都會瞬間彈出 tooltip。
    const enterTimer = React.useRef(null);
    const cancelEnter = React.useCallback(() => {
        if (enterTimer.current) {
            clearTimeout(enterTimer.current);
            enterTimer.current = null;
        }
    }, []);
    React.useEffect(() => cancelEnter, [cancelEnter]);

    const handleEnter = () => {
        cancelEnter();
        enterTimer.current = setTimeout(() => setHovered(true), HOVER_DELAY_MS);
    };
    const handleLeave = () => {
        cancelEnter();
        setHovered(false);
    };

    // 「重設」只在這天真的被人工指定過才有意義 —— 沒有 override
    // 可清，選了也不會改變任何東西，disabled 讓使用者一眼看出這件事，
    // 不必點下去才發現什麼都沒發生。用分隔線跟五個級別隔開，避免被誤讀成
    // 「第六個級別」。
    const hasOverride = day.state === 'manual';

    // 「套用建議」是這一格的捷徑，等同於到調整建議清單按該日的「採納」——
    // 使用者逐格看日曆時發現藍框 (有建議)，不必為了採納一天而去開 Drawer 翻找。
    // 兩個條件都要成立才可按：這天真的有建議 (suggested 與 base 不同)，而且
    // 還沒套用 (現在的級別不等於建議級別，涵蓋「已經採納過」的情況)。
    const hasSuggestion = day.suggested_level && day.suggested_level !== day.base_level;
    const canAdopt = hasSuggestion && day.level !== day.suggested_level;
    const menu = (
        <Menu
            onClick={({ key }) => {
                // rc-dropdown 點選單項目時是直接 setTriggerVisible(false) 收掉浮層，
                // 不會呼叫 onVisibleChange (見 rc-dropdown/lib/Dropdown.js 的 onClick)，
                // 只有點外面或按 ESC 才會通知外界。所以選完級別要自己回報關閉，
                // 否則外層會一直以為選單還開著，把整年的 tooltip 都關住。
                onMenuVisibleChange(date, false);
                if (key === RESET_KEY) {
                    onReset(date);
                    return;
                }
                if (key === ADOPT_KEY) {
                    onChangeLevel(date, day.suggested_level);
                    return;
                }
                onChangeLevel(date, key);
            }}
        >
            {/* 色塊放在文字前面：選單是「挑顏色」的動作，日曆上看到的是底色而不是
                名稱，先給顏色再給名稱才對得起來，也跟頁面上的圖例同一套視覺語言。 */}
            {LEVELS.map((level) => (
                <Menu.Item key={level}>
                    <LevelSwatch level={level} />
                    {LEVEL_LABELS[level]}
                </Menu.Item>
            ))}
            <Menu.Divider />
            {/* 帶出建議的級別名稱與色塊，按下去會變成什麼一目了然，
                不必先關掉選單去看 tooltip 才知道建議是什麼。 */}
            <Menu.Item key={ADOPT_KEY} disabled={!canAdopt}>
                {hasSuggestion ? <LevelSwatch level={day.suggested_level} /> : null}
                {hasSuggestion ? `套用建議：${LEVEL_LABELS[day.suggested_level]}` : '套用建議'}
            </Menu.Item>
            <Menu.Item key={RESET_KEY} disabled={!hasOverride}>
                重設
            </Menu.Item>
        </Menu>
    );

    // 節日標記與級別無關，獨立判斷 —— 見上方 docblock：連假最後一天的
    // 國定假日級別可能是 normal，底色看不出來，得靠這個屬性單獨標出來。
    const specialAttrs = day.holiday_name ? { 'data-special': 'true' } : {};

    // 螢幕閱讀器讀不到底色／邊框／黑點這些視覺通道，accessible name 要把
    // 「哪一天、什麼級別」講出來；節日名稱一併帶上，等同於底線通道的資訊。
    const ariaLabel = day.holiday_name
        ? `${date}，${LEVEL_LABELS[day.level]}，${day.holiday_name}`
        : `${date}，${LEVEL_LABELS[day.level]}`;

    // Tooltip 包 Dropdown、Dropdown 直接包 DOM 節點 (不是反過來)：rc-dropdown
    // 選完選單項目後排一個 setTimeout 直接對 triggerRef.current.triggerRef.current
    // 呼叫 .focus()，不會呼叫 findDOMNode() 轉一手。若 Dropdown 的直接子節點是
    // Tooltip (一個元件而非 DOM 節點)，拿到的會是 rc-tooltip 內部自己的 Trigger
    // 實例 (它的 ref 轉發到 domRef.current，仍是元件實例，不是 DOM 節點)，
    // 該實例沒有 .focus()，才會丟出 TypeError: n.focus is not a function。
    // Dropdown 直接包住這個 div，triggerRef.current.triggerRef.current 才會是
    // 真正的 DOM 節點。Tooltip 移到最外層不受影響：它自己的定位邏輯全部走
    // findDOMNode()，能正確穿透中間的元件層級。
    return (
        <Tooltip
            title={<DayTip day={day} />}
            visible={hovered && !anyMenuOpen}
            overlayStyle={{ pointerEvents: 'none' }}
        >
            <Dropdown
                overlay={menu}
                trigger={['click']}
                onVisibleChange={(open) => onMenuVisibleChange(date, open)}
            >
                <div
                    data-testid={`day-${date}`}
                    data-level={day.level}
                    data-state={day.state}
                    {...specialAttrs}
                    tabIndex={0}
                    role="button"
                    aria-label={ariaLabel}
                    // rc-trigger 用 createTwoChains 串接子節點自己的滑鼠事件
                    // (rc-trigger/lib/index.js)，這幾個 handler 不會被它蓋掉。
                    onMouseEnter={handleEnter}
                    onMouseLeave={handleLeave}
                    // 按下去代表使用者要開選單、不是要讀 tooltip。主動把 hover 狀態
                    // 收掉，選單關閉後 tooltip 才不會又自己彈回來閃一下 —— 想再看
                    // 就把滑鼠移開再移回來。
                    onClick={handleLeave}
                    // rc-dropdown 開啟後自己接管 ESC / TAB (見 useAccessibility)，
                    // 但開啟本身只在收到瀏覽器原生 click 事件時才會發生 —— div
                    // 沒有 <button> 天生的「Enter / Space 觸發 click」行為，要自己補。
                    // 用 currentTarget.click() 而不是直接呼叫選單邏輯，好讓鍵盤與
                    // 滑鼠觸發都走同一條路徑，不必假設 Dropdown 內部怎麼接線。
                    onKeyDown={(event) => {
                        if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            event.currentTarget.click();
                        }
                    }}
                    style={{
                        position: 'relative',
                        textAlign: 'center',
                        cursor: 'pointer',
                        padding: '2px 0',
                        fontSize: FONT.caption,
                        lineHeight: '18px',
                        backgroundColor: LEVEL_COLORS[day.level],
                        color: TEXT_COLOR[day.level] || 'rgba(0,0,0,0.85)',
                        border: day.state === 'suggested' ? SUGGESTED_BORDER : '1px solid #f0f0f0',
                    }}
                >
                    <span
                        data-testid={`day-number-${date}`}
                        style={
                            day.holiday_name
                                ? { textDecoration: 'underline', fontWeight: 700 }
                                : undefined
                        }
                    >
                        {Number(date.slice(8, 10))}
                    </span>
                    {day.state === 'manual' ? <span style={MANUAL_DOT_STYLE} /> : null}
                </div>
            </Dropdown>
        </Tooltip>
    );
}

/**
 * 12 個月的日曆網格，版面與飯店公告表同構 —— 最後要照著它做公告，
 * 網格長得一樣才對得起來。
 *
 * 每一格有四個視覺通道，各自獨立、互不干擾：
 * - 底色：價格級別 (平日 / 旺日 / 假日 / 旺假日 / 大假日)。
 * - 藍色邊框：資料對這天有建議 (尚未採納)。底色仍是規則算出的級別，建議並未
 *   套用；要接受建議請到下面的建議清單按「採納」，那才會變成人工指定 (右上角
 *   的點) 並真的改變底色。
 * - 右上角黑點：人工指定過，一律覆蓋規則與建議。
 * - 日期數字加底線：這天有節日名稱 (holiday_name，國定假日、補假、小年夜、
 *   除夕等)。這個通道跟級別**正交**，不是級別的附屬品 —— 有節日名稱不代表
 *   級別高，例如連假最後一天的國定假日常常仍是平日 (旅客已陸續回程)，這正是
 *   使用者需要底線提醒的原因：光看底色會以為那天沒什麼特別，不逐格 hover
 *   看不出它其實是節日。
 */
export default function YearCalendar({ year, days, onChangeLevel, onReset }) {
    const byDate = React.useMemo(
        () => Object.fromEntries((days || []).map((day) => [day.date, day])),
        [days]
    );

    // 哪一格的級別選單正開著。存日期而不是布林值，是因為「關掉 A、打開 B」時
    // 兩個 onVisibleChange 的先後順序不保證，用日期比對才不會被後到的 false 誤關。
    const [openDate, setOpenDate] = React.useState(null);
    const anyMenuOpen = openDate !== null;

    const handleMenuVisibleChange = React.useCallback((date, open) => {
        setOpenDate((prev) => (open ? date : prev === date ? null : prev));
    }, []);

    const cell = (date) => {
        const day = byDate[date];
        // CSS Grid 的自動排版數的是實際渲染出來的子節點，不是陣列長度 ——
        // 缺日若回傳 null，之後每一格都會往前遞補一格，整月靜靜錯位且看不出來，
        // 所以缺日要佔住位置，用空白格顯示「這裡少了資料」。
        if (!day) return <div key={date} />;

        return (
            <DayCell
                key={date}
                date={date}
                day={day}
                anyMenuOpen={anyMenuOpen}
                onMenuVisibleChange={handleMenuVisibleChange}
                onChangeLevel={onChangeLevel}
                onReset={onReset}
            />
        );
    };

    const month = (m) => {
        const cells = [];
        for (let i = 0; i < leadingBlanks(year, m); i++) {
            cells.push(<div key={`blank-${m}-${i}`} />);
        }
        for (let d = 1; d <= daysInMonth(year, m); d++) {
            cells.push(cell(`${year}-${pad(m)}-${pad(d)}`));
        }

        return (
            <div key={m} style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>{m} 月</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 1 }}>
                    {WEEKDAY_HEADERS.map((label) => (
                        <div key={label} style={{ textAlign: 'center', fontSize: FONT.caption, opacity: 0.55 }}>
                            {label}
                        </div>
                    ))}
                    {cells}
                </div>
            </div>
        );
    };

    // 固定三個月一列 (共四列)，與飯店公告表以及後端 CalendarExporter 的 MONTHS_PER_ROW 相同 ——
    // 使用者最後要照著這張表做對外公告，排法一樣才對得起來。
    // 不用 auto-fit：那會隨視窗寬度變成 4、6 或 8 個一列，跟公告表對不上。
    //
    // 固定三欄配上窄視窗會把每個月壓到很窄，7 欄日期格擠成一團看不清 ——
    // 所以外層包 overflow-x: auto，內層網格給一個看得清楚的最小寬度，
    // 窄視窗改出現水平捲軸，而不是繼續往內壓縮。
    return (
        <div style={{ overflowX: 'auto' }}>
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
                    gap: 16,
                    minWidth: 540,
                }}
            >
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month)}
            </div>
        </div>
    );
}
